import type { TooltipProps } from '@mui/material';
import { inputClasses } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import type { FixedProductInput } from '@weft/types';
import { isNotNullOrUndefined, withDefault } from '@weft/utils';
import { get } from 'lodash';
import type { ComponentProps } from 'react';
import { useRef, useState } from 'react';
import type { FieldPath } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { VayuTextField } from '../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useIsOverflowed } from '../../../../../../hooks/use-is-overflowed';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';

type EditCellProps = {
  fixedProduct: FixedProductInput;
  field: FieldPath<FixedProductInput>;
  tooltipPlacement?: TooltipProps['placement'];
  valueAsNumber?: boolean;
} & ComponentProps<typeof VayuTextField>;

export const ProductEditCell: React.FC<EditCellProps> = ({ fixedProduct, field, tooltipPlacement = 'top', valueAsNumber = false, ...props }) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { register, getValues, formState: { errors } } = useFormContext<PlanInputForm, NewPlanForm>();
  const products = withDefault(getValues('fixedProducts'), []);
  const productIndex = products.findIndex((product) => product.id === fixedProduct.id);

  const registerField = register(`fixedProducts.${productIndex}.${field}`, {
    valueAsNumber,
  });
  registerErrorField(registerField.name, CREATE_PLAN_TABS.serviceFees);

  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const editCellRef = useRef<HTMLInputElement>(null);
  const isInputOverflowed = useIsOverflowed(editCellRef);

  return (
    <Tooltip
      title={getValues(`fixedProducts.${productIndex}.${field}`)?.toString() ?? ''}
      placement={tooltipPlacement}
      arrow
      open={!isEditing && isHovering && isInputOverflowed && !Number.isNaN(getValues(`fixedProducts.${productIndex}.${field}`))}
    >
      <VayuTextField
        {...registerField}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{
          ...props.sx,
          [`& .${inputClasses.root}`]: {
            border: 'none',
            ['&.Mui-focused']: {
              border: '1px solid #E1E6E8',
              padding: '4px 16px !important',
            },
          },
        }}
        inputRef={editCellRef}
        {...props}
        onFocus={(event) => {
          setIsEditing(true);
          props.onFocus?.(event);
        }}
        onBlur={(event) => {
          setIsEditing(false);
          props.onBlur?.(event);
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        error={isNotNullOrUndefined(get(errors, registerField.name))}
      />
    </Tooltip>
  );
};